import React, { useCallback, useEffect, useState } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import CommonInvoiceTemplate from '../../../../documents/components/CommonInvoiceTemplate'
import { SingleCreditNoteData, TableColumn } from '../../../../types'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { request } from '../../../../../../utils/request'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import PrintButtons from '../../../../../../components/PrintButtons'
import usePrint from '../../../../../../utils/usePrint'
import PrintableContainer from '../../../../../../components/PrintableContainer'
import { BusinessInfo, TaxInfo } from '../../../../../controlPanel/types'

const ShowCreditNote = () => {
  const [loading, setLoading] = useState(true);
  const [singleCreditNote, setCreditNote] = useState<SingleCreditNoteData | null>(null);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>();
  const [taxInfo, setTaxInfo] = useState<TaxInfo>();
  const params = useParams()
  const creditNoteId = params.creditNoteId

  const { isPrinting, handlePrint } = usePrint();

  const fetchBusinessInfo = useCallback(async () => {
    try {
      const res = await request({
        url: "setting/businessInfo",
        method: "get",
      });

      if (res.success) {
        setBusinessInfo(res.data);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to fetch business info");
    }
  }, []);
  useEffect(() => {
    fetchBusinessInfo();
  }, [fetchBusinessInfo])

  const fetchTaxInfo = useCallback(async () => {
    try {
      const res = await request({
        url: "setting/taxInfo",
        method: "get",
      });

      if (res.success) {
        setTaxInfo(res.data);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to fetch tax info");
    }
  }, []);
  useEffect(() => {
    fetchTaxInfo();
  }, [fetchTaxInfo])

  const fetchSingleCreditNote = useCallback(async () => {
    if (!creditNoteId) return;

    setLoading(true)
    try {
      const res = await request({
        url: `creditNote/get-single-credit-note/${creditNoteId}`,
        method: "get",
      })
      if (res.success && res.data) {
        setCreditNote(res.data)
      } else {
        toast.error(res.message || "Failed to fetch Credit note");
      }
    } catch (error: any) {
      toast.error(error.message || "Error fetching Credit note");
    } finally {
      setLoading(false)
    }
  }, [creditNoteId])

  useEffect(() => {
    fetchSingleCreditNote();
  }, [fetchSingleCreditNote])

  // Format date to readable format
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const onPrint = () => {
    // force re-render before invoking print
    requestAnimationFrame(() => {
      setTimeout(() => {
        handlePrint('credit_note_print', {
          title: `Credit Note - ${singleCreditNote?.creditNoteNumber}'}`,
          fileName: `Sales _${singleCreditNote?.creditNoteNumber}`,
          landscape: false,
          showHeader: true,
          showFooter: false,
          customStyles: `
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #ddd !important; padding: 6px !important; }
          th { background-color: #f5f5f5 !important; font-weight: bold !important; }
          .no-print { display: none !important; }
          .invoice-heading {text-align:center; font-size: 22px}
          .flex-center {display:flex; justify-content:space-between; align-items: flex-start; gap:5px}
          .sub-heading {font-size:14px; font-weight:bold}
          .text-bold {font-weight:bold}
          .w-50{width:50%}
          .out-border{border:1px solid black}
        `
        });
      }, 150); // small delay ensures browser repaints
    });
  };

  // Calculate totals from invoice items
  const calculateTotals = () => {
    if (!singleCreditNote?.items?.length) {
      return {
        quantity: 0,
        taxableAmount: 0,
        tax: 0,
        amount: 0
      };
    }

    const totals = singleCreditNote.items.reduce((acc, item) => {
      acc.quantity += item.quantity || 0;
      acc.amount += item.amount || 0;
      // Calculate tax amount based on item amount and tax rate
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      acc.tax += taxAmount;
      // Taxable amount is amount minus tax (for GST calculations)
      acc.taxableAmount += (item.amount || 0) - taxAmount;
      return acc;
    }, { quantity: 0, taxableAmount: 0, tax: 0, amount: 0 });

    return totals;
  };

  const prepareTableData = () => {
    if (!singleCreditNote?.items?.length) return [];

    return singleCreditNote.items.map((item, index) => {
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      return {
        id: item.id || index.toString(),
        item: item.itemName || `Item ${index + 1}`,
        hsnNo: item.hsnNo || 'N/A',
        quantity: item.quantity || 0,
        rate: item.price || 0,
        tax: taxAmount,
        taxPercentage: item.tax?.rate || 0,
        amount: item.amount || 0,
        discount: item.discount?.amount || 0,
        discountPercentage: item.discount?.isPercentage ? item.discount.amount : 0
      };
    });
  };

  // Prepare table columns based on GST type
  const prepareTableColumns = (): TableColumn[] => {
    const baseColumns: TableColumn[] = [
      // { id: 'srNo', label: 'Sr No', align: "left", width: 60 },
      { id: 'item', label: 'Item Description', align: "left", width: 200 },
      { id: 'quantity', label: 'Qty', align: "right", width: 80 },
      { id: 'rate', label: 'Rate (₹)', align: "right", width: 100 },
      { id: 'discount', label: 'Discount (₹)', align: "right", width: 100 },
    ];

    if (singleCreditNote?.gstType === 'GST') {
      // baseColumns.push({ id: 'hsnNo', label: 'HSN/SAC', align: "left", width: 100 });
    }

    baseColumns.push(
      // { id: 'taxPercentage', label: 'Tax %', align: "right", width: 80 },
      { id: 'tax', label: 'Tax Amt (₹)', align: "right", width: 100 },
      { id: 'amount', label: 'Amount (₹)', align: "right", width: 120 }
    );

    return baseColumns;
  };

  // Prepare summary items
  const prepareSummaryItems = () => {
    if (!singleCreditNote) return [];

    const totals = calculateTotals();
    const summaryItems = [];

    // Taxable Amount
    summaryItems.push({
      label: 'Taxable Amount',
      value: singleCreditNote?.taxableAmount,
      variant: 'body2' as const
    });

    // GST Breakdown if GST credit note
    if (singleCreditNote.gstType === 'GST' && singleCreditNote.taxBreakdown?.length > 0) {
      singleCreditNote.taxBreakdown.forEach((taxItem, index) => {
        if (taxItem.cgst > 0) {
          summaryItems.push({
            label: `CGST (${taxItem.cgst}%)`,
            value: (totals.taxableAmount * taxItem.cgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.sgst > 0) {
          summaryItems.push({
            label: `SGST (${taxItem.sgst}%)`,
            value: (totals.taxableAmount * taxItem.sgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.igst > 0) {
          summaryItems.push({
            label: `IGST (${taxItem.igst}%)`,
            value: (totals.taxableAmount * taxItem.igst) / 100,
            variant: 'body2' as const
          });
        }
      });
    } else if (singleCreditNote.gstType === 'GST') {
      // Fallback if no tax breakdown
      summaryItems.push({
        label: `GST (${singleCreditNote.items[0]?.tax?.rate || 0}%)`,
        value: totals.tax,
        variant: 'body2' as const
      });
    }

    // Additional Charges
    if (singleCreditNote.charges?.length > 0) {
      singleCreditNote.charges.forEach(charge => {
        summaryItems.push({
          label: charge.label,
          value: charge.amount,
          variant: 'body2' as const
        });
      });
    }

    // Discount
    if (singleCreditNote.discount?.amount && singleCreditNote.discount.amount > 0) {
      const discountLabel = singleCreditNote.discount.isPercentage
        ? `Discount (${singleCreditNote.discount.amount}%)`
        : 'Discount';
      summaryItems.push({
        label: discountLabel,
        value: singleCreditNote.discount.amount,
        variant: 'body2' as const
      });
    }

    // Round Off
    if (singleCreditNote.roundOff) {
      const roundOffValue = Math.round(totals.amount + totals.tax) - (totals.amount + totals.tax);
      if (Math.abs(roundOffValue) > 0) {
        summaryItems.push({
          label: 'Round Off',
          value: roundOffValue,
          variant: 'body2' as const
        });
      }
    }

    // Grand Total
    const grandTotal = singleCreditNote.totalAmount;
    summaryItems.push({
      label: 'Grand Total',
      value: grandTotal,
      bold: true,
      variant: 'body1' as const
    });

    return summaryItems;
  };

  // Prepare custom fields for payment info
  const prepareCustomFields = () => {
    if (!singleCreditNote) return [];

    const fields = [];

    if (singleCreditNote.receivedAmount >= 0) {
      fields.push({
        label: "Paid Amount",
        value: singleCreditNote.receivedAmount
      });
    }

    if (singleCreditNote.balanceAmount >= 0) {
      fields.push({
        label: "Balance Due",
        value: singleCreditNote.balanceAmount
      });
    }

    return fields;
  };

  if (!singleCreditNote && !loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <DocumentHeader
          heading='Credit Note'
          subtitle='Credit Note not found'
          showBackButton={true}
        />
      </Box>
    );
  }

  const totals = calculateTotals();
  const tableData = prepareTableData();
  const tableColumns = prepareTableColumns();
  const summaryItems = prepareSummaryItems();
  const customFields = prepareCustomFields();

  return (
    <>
      <DocumentHeader
        heading='Credit Note'
        subtitle={`#${singleCreditNote?.creditNoteNumber || 'Loading...'}`}
        status={singleCreditNote?.status || 'UNPAID'}
        statusColor={singleCreditNote?.status === 'PAID' ? 'success' :
          singleCreditNote?.status === 'PARTIAL_PAID' ? 'info' : singleCreditNote?.status === 'UNPAID' ? 'warning' : 'primary'}
        showBackButton={true}
      />

      <Box display='flex' justifyContent='flex-end' mb={3} gap={2}>

        <PrintButtons
          onPrint={onPrint}
          isPrinting={isPrinting}
          printLabel='Print CN'
          pdfLabel='Export PDF'
          disabled={loading || !singleCreditNote}
          showPDF={false} // Remove PDF button since we don't have export functionality yet
        />
        {/* <CommonCreateButton
          label="Record Payment"
          onClick={() => alert('Record Payment clicked')}
          disabled={singleCreditNote?.status === 'PAID'}
        /> */}
      </Box>

      <PrintableContainer id="credit_note_print">
        <CommonInvoiceTemplate
          documentType={singleCreditNote?.creditNoteType || "CREDIT_NOTE"}
          title={singleCreditNote?.gstType === 'GST' ? "CREDIT NOTE" : "CREDIT NOTE"}
          companyName={businessInfo?.businessName}
          companyAddress={
            (businessInfo?.address) ? (`${businessInfo.address?.[0]?.line1} ${businessInfo.address?.[0]?.city} ${businessInfo.address?.[0]?.state}  ${businessInfo.address?.[0]?.pinCode}`) : 'Address not mentioned'
          }
          companyPhone={(businessInfo?.contact?.phone?.[0]?.phoneNo) ? (`${businessInfo?.contact?.phone?.[0]?.phoneNo}`) : '+91 0000000000'}
          companyGSTIN={singleCreditNote?.gstType === 'GST' ? taxInfo?.taxDetails?.gstNumber : ""}
          documentNumber={singleCreditNote?.creditNoteNumber || ''}
          documentDate={formatDate(singleCreditNote?.creditNoteDate || singleCreditNote?.date || '')}
          dueDate={formatDate(singleCreditNote?.dueDate || '')}
          partyHeading='Party Info.'
          partyName={singleCreditNote?.party?.partyName || 'N/A'}
          partyLocation={singleCreditNote?.party?.location}
          partyAddress={
            (singleCreditNote?.party?.address) ? (`${singleCreditNote.party.address.line1} ${singleCreditNote.party.address.city} ${singleCreditNote.party.address.state}  ${singleCreditNote.party.address.pinCode}`) : 'Address not available'
          }
          partyPhone={singleCreditNote?.party?.contact?.phone?.[0]?.phoneNo || 'N/A'}
          partyGSTIN={singleCreditNote?.party?.gstNumber || 'N/A'}
          tableColumns={tableColumns}
          tableData={tableData}
          tableTotals={{
            quantity: totals.quantity,
            taxableAmount: totals.taxableAmount,
            tax: totals.tax,
            amount: totals.amount
          }}
          amounts={{
            totalAmount: singleCreditNote?.totalAmount,
            taxableAmount: singleCreditNote?.taxableAmount,
            subtotal: singleCreditNote?.subtotal,
            receivedAmount: singleCreditNote?.receivedAmount,
            balanceAmount: singleCreditNote?.balanceAmount
          }}
          summaryItems={summaryItems}
          customFields={customFields}
          showLoading={loading}
          showLoadingText="Fetching Credit Notes..."
          // notes={singleInvoice?.notes || ''}
          termsConditions={singleCreditNote?.terms || ''}
          gstType={singleCreditNote?.gstType}
          invoiceStatus={singleCreditNote?.status}
        />
      </PrintableContainer>
    </>
  )
}

export default ShowCreditNote